import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";
import { apiFetch, mediaUrl } from "../utils/api";
import { formatDate } from "../utils/time";
import { useAuth } from "../context/AuthContext";
import "../Content.css";
import "./ContentModal.css";

/**
 * Updates and policies open in a MODAL, in place — not a new page. Blog still navigates
 * to its own reading page (posts are long-form with rich block layouts).
 *
 * Any component can call `openContent("update" | "policy", id)`. The modal fetches the
 * same detail endpoint the standalone page uses, so the audience filter is still
 * re-checked SERVER-SIDE per row (a deep-linked /update/:id page also still works, and
 * so do notification click-throughs). This is presentation only — it changes where the
 * detail is shown, not who is allowed to see it.
 */
const ContentModalContext = createContext({ openContent: () => {} });
export const useContentModal = () => useContext(ContentModalContext);

const CONFIG = {
    update: { endpoint: (id) => `/api/Dolluzcorp/updates/${id}`, chip: "Update", kind: "update" },
    policy: { endpoint: (id) => `/api/Dolluzcorp/policies/${id}`, chip: "Policy", kind: "policy" },
};

export const ContentModalProvider = ({ children }) => {
    const [target, setTarget] = useState(null); // { type, id }

    const openContent = useCallback((type, id) => {
        if (CONFIG[type] && id != null) setTarget({ type, id });
    }, []);
    const close = useCallback(() => setTarget(null), []);

    return (
        <ContentModalContext.Provider value={{ openContent }}>
            {children}
            {target && <ContentModalHost target={target} onClose={close} />}
        </ContentModalContext.Provider>
    );
};

const ContentModalHost = ({ target, onClose }) => {
    const { isAuthenticated } = useAuth();
    const cfg = CONFIG[target.type];

    const [data, setData] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | ready | notfound

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setStatus("loading");
            setData(null);
            try {
                const res = await apiFetch(cfg.endpoint(target.id));
                const json = await res.json();
                if (cancelled) return;

                if (res.ok && json.success) { setData(json.data); setStatus("ready"); }
                else setStatus("notfound");
            } catch {
                if (!cancelled) setStatus("notfound");
            }
        })();

        return () => { cancelled = true; };
        // Signing in can bring an item into this viewer's audience.
    }, [cfg, target.id, isAuthenticated]);

    // Escape to close, and lock the page scroll while the modal is up.
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    /**
     * images_max_height is a HEIGHT (not a max-height), applied to every image in the
     * update — same rule as the standalone page, so the modal and the page agree.
     */
    const imgCap =
        target.type === "update" && Number(data?.images_max_height) > 0
            ? { height: `${Number(data.images_max_height)}px`, width: "auto", maxWidth: "100%", objectFit: "contain" }
            : undefined;

    return createPortal(
        <div
            className="doc-modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="doc-modal-title"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="doc-modal">
                <button type="button" className="doc-modal-close" onClick={onClose} aria-label="Close">
                    <FaTimes aria-hidden="true" />
                </button>

                <div className="doc-modal-scroll">
                    {status === "loading" && (
                        <div className="doc-modal-state">
                            <span className="spinner spinner-dark" aria-hidden="true" />
                            <span id="doc-modal-title">Loading…</span>
                        </div>
                    )}

                    {status === "notfound" && (
                        <div className="doc-modal-state doc-modal-empty">
                            <span className="empty-icon" aria-hidden="true">🔍</span>
                            <h2 id="doc-modal-title">This {cfg.kind} isn't available</h2>
                            <p>It may have been moved or removed.</p>
                        </div>
                    )}

                    {status === "ready" && data && (
                        <article>
                            <header className="article-head">
                                <span className={`chip chip-${cfg.kind}`}>{cfg.chip}</span>
                                <h1 id="doc-modal-title" className="article-title">{data.title}</h1>
                                {(data.subject || data.excerpt) && (
                                    <p className="article-standfirst">{data.subject || data.excerpt}</p>
                                )}
                                <div className="article-meta">
                                    {target.type === "policy" && <span>Last updated</span>}
                                    <time dateTime={data.updated_time || data.created_time}>
                                        {formatDate(data.updated_time || data.created_time)}
                                    </time>
                                </div>
                            </header>

                            {/* Update images lead the body, above the description — matching
                                dAdmin's preview and the standalone page. */}
                            {target.type === "update" && data.images?.length > 0 && (
                                <div className="article-figures">
                                    {data.images.map((path, i) => (
                                        <img
                                            key={i}
                                            src={mediaUrl(path)}
                                            alt={`Attachment ${i + 1} for ${data.title}`}
                                            loading="lazy"
                                            style={imgCap}
                                        />
                                    ))}
                                </div>
                            )}

                            <div
                                className="prose article-body"
                                dangerouslySetInnerHTML={{ __html: data.description }}
                            />
                        </article>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};
